import { DOCUMENT, inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

export interface PageSeo {
  title: string;
  description: string;
  path: string;                 // route path, leading slash, no origin
  image?: string;
  type?: 'website' | 'article' | 'product';
  noindex?: boolean;
}

export const SITE_ORIGIN = 'https://mechtronwaterservices.com';

/**
 * Single owner of every SEO-relevant tag. Components never touch Meta directly —
 * that is how duplicate and stale tags creep in on SPA navigation.
 * Runs identically on the server, so prerendered HTML carries the final tags.
 */
@Injectable({ providedIn: 'root' })
export class Seo {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly doc = inject(DOCUMENT);

  apply(seo: PageSeo): void {
    const url = SITE_ORIGIN + seo.path;
    const image = seo.image
      ? (seo.image.startsWith('http') ? seo.image : SITE_ORIGIN + seo.image)
      : SITE_ORIGIN + '/assets/images/waste-water-treatment-plant.jpg';

    this.title.setTitle(seo.title);
    this.meta.updateTag({ name: 'description', content: seo.description });
    this.meta.updateTag({
      name: 'robots',
      content: seo.noindex ? 'noindex,nofollow' : 'index,follow,max-image-preview:large'
    });

    this.meta.updateTag({ property: 'og:title', content: seo.title });
    this.meta.updateTag({ property: 'og:description', content: seo.description });
    this.meta.updateTag({ property: 'og:type', content: seo.type ?? 'website' });
    this.meta.updateTag({ property: 'og:url', content: url });
    this.meta.updateTag({ property: 'og:image', content: image });
    this.meta.updateTag({ property: 'og:site_name', content: 'Mechtron Water Services' });
    this.meta.updateTag({ property: 'og:locale', content: 'en_IN' });

    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: seo.title });
    this.meta.updateTag({ name: 'twitter:description', content: seo.description });
    this.meta.updateTag({ name: 'twitter:image', content: image });

    this.setCanonical(url);
  }

  private setCanonical(url: string): void {
    let link = this.doc.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.doc.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.doc.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }

  /** JSON-LD via textContent — never innerHTML, so a payload can never be parsed as markup. */
  setJsonLd(id: string, data: object): void {
    this.doc.getElementById(id)?.remove();
    const script = this.doc.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(data);
    this.doc.head.appendChild(script);
  }

  clearJsonLd(id: string): void {
    this.doc.getElementById(id)?.remove();
  }
}
